import React, { useState, useContext, useEffect, useCallback } from 'react';
import { ThemeOptions } from '@mui/material';
import { useLocation } from 'react-router';
import { Palette } from 'src/features/theme/palette/types';
import { Theme } from 'src/features/theme/type';
import { ThemeTypeContextTypes, ThemeTypeProviderPropsTypes } from './type';
import { useCustomer } from '../customerContext/customer.context';
import { ThemeService } from 'src/services/themes/theme.service';

const ThemeTypeContext = React.createContext<ThemeTypeContextTypes>({} as ThemeTypeContextTypes);

export const useThemeType = () => useContext(ThemeTypeContext);

const defaultAppTheme: Palette = {
  lighter: '#CDF0F0',
  light: '#69D3D1',
  main: '#05B6B2',
  dark: '#398599',
  darker: '#005249',
  contrastText: '#fff',
};
const defaultSidebarTheme: ThemeOptions['sidebar'] = {
  background: '#21374E',
  unselectedText: '#BCC7D1',
  selectedSiteBackground: '#2B4764',
  selectedNavItemBackground: '#3A5776',
  alarmNotificationColor: '#FF4842',
};

export const ThemeTypeProvider = ({ children }: ThemeTypeProviderPropsTypes) => {
  const [themeType, setThemeType] = useState<'light' | 'dark'>('light');

  const toggleThemeType = () => {
    setThemeType((prevValue) => (prevValue === 'light' ? 'dark' : 'light'));
  };

  const [customerTheme, setCustomerTheme] = useState<Theme | null | {}>({
    primary: defaultAppTheme,
    sidebar: defaultSidebarTheme,
  });
  const [customerThemeLoaded, setCustomerThemeLoaded] = useState<boolean>(false);

  const location = useLocation();
  const customerId = location.search.slice(12, 38);
  const { themeIds } = useCustomer(customerId);

  const fetchTheme = useCallback(async () => {
    if (!customerId || themeIds?.value.activeThemeId === '' || themeIds?.loading) {
      await ThemeService.getById('1', '1', false)
        .then((data) => {
          setCustomerTheme({
            id: data.id,
            customerId: data.customerId,
            themeName: data.themeName,
            primary: data.primary,
            sidebar: data.sidebar,
          });
          setCustomerThemeLoaded(true);
          return;
        })
        .catch((error) => {
          setCustomerTheme({
            id: '1',
            customerId: '1',
            themeName: 'default',
            primary: defaultAppTheme,
            sidebar: defaultSidebarTheme,
          });
          setCustomerThemeLoaded(true);
          console.error('Server Error', error);
        });
    }

    try {
      if (
        customerId &&
        themeIds?.value &&
        !themeIds.loading &&
        themeIds.value.activeThemeId !== undefined
      ) {
        await ThemeService.getById(customerId, themeIds.value.activeThemeId, false)
          .then((data) => {
            setCustomerTheme({
              id: data.id,
              customerId: data.customerId,
              themeName: data.themeName,
              primary: data.primary,
              sidebar: data.sidebar,
            });
            setCustomerThemeLoaded(true);
          })
          .catch((error) => {
            setCustomerTheme({
              id: '1',
              customerId: '1',
              themeName: 'default',
              primary: defaultAppTheme,
              sidebar: defaultSidebarTheme,
            });
            setCustomerThemeLoaded(true);
            console.error('Server error', error);
          });
      }
    } catch (error) {
      console.error(error);
    }
  }, [customerId, themeIds]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  return (
    <ThemeTypeContext.Provider
      value={{
        themeType,
        toggleThemeType,
        customerTheme,
        customerThemeLoaded,
      }}
    >
      {children}
    </ThemeTypeContext.Provider>
  );
};
